'use client'

import React, { useEffect, useState } from 'react'
import { usePathname, useSearchParams, useRouter, ReadonlyURLSearchParams } from 'next/navigation'
import { TabordionTypes } from '@dustin-web/microsite-components/src/tabordion'

import { useLayout } from '@dustin-web/microsite-components/src/hooks'
import { TabordionItemType } from '@dustin-web/microsite-components/src/tabordion'
import {
  ProductPageTabName,
  ProductPageTabNames,
} from '@productsite-beta/src/components/product-page'

export type Props = {
  tabQueryParam: ProductPageTabName | null
  scrollToTabQueryParam: boolean
  tabordionItems: TabordionItemType[]
  activeTab: number
  fallbackToAccessoriesTab?: boolean
}

const ProductPageTabordionClient = ({
  scrollToTabQueryParam,
  tabordionItems,
  activeTab,
  tabQueryParam,
  fallbackToAccessoriesTab,
}: Props) => {
  const { responsive } = useLayout()
  const queryState = useSearchParams()
  const [activeTabNumber, setActiveTabNumber] = useState(activeTab)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()

  useEffect(() => {
    // load accessorises after page load
    if (!queryState?.get('tab') && fallbackToAccessoriesTab) {
      const params = searchParams ? removeSearchParam(searchParams, 'scrollToTab') : ''
      const href = pathname + '?' + params + '&tab=accessories'
      router.replace(href, { scroll: false })
      setActiveTabNumber(2)
    }
  }, [])

  useEffect(() => {
    const currentTab = queryState?.get('tab')
    if (currentTab && tabQueryParam && activeTab !== activeTabNumber) {
      setActiveTabNumber(activeTab)
    }
  }, [tabQueryParam, activeTab, activeTabNumber, queryState])

  function isValidProductPageTabName(value: string | null): value is ProductPageTabName {
    return ProductPageTabNames.includes(value as ProductPageTabName)
  }

  const validQueryParam = isValidProductPageTabName(tabQueryParam) ? tabQueryParam : null

  return (
    <ScrollToContainer
      scrollToTabQueryParam={scrollToTabQueryParam}
      tabQueryParam={validQueryParam}>
      <TabordionTypes
        id="product-page-tabordion"
        sticky={responsive.breakpoints.min.MD}
        mega
        links
        noContentPadding
        items={tabordionItems}
        extState
        activeTabs={[activeTabNumber]}
        tabsClassName="u-bt"
        accordionContainerClassName="product-accordion"
        accordionFirstandLastBorder
        useExtToggleItem
        onTabClick={tabNumber => {
          const url = tabordionItems[tabNumber].href || ''
          setActiveTabNumber(tabNumber)
          window.history.pushState({}, '', url)
        }}
      />
    </ScrollToContainer>
  )
}

function removeSearchParam(params: ReadonlyURLSearchParams, paramToRemove: string): string {
  const searchParams = new URLSearchParams(params.toString())
  searchParams.delete(paramToRemove)
  return searchParams.toString()
}

export function ScrollToContainer({
  children,
  scrollToTabQueryParam,
  tabQueryParam,
}: {
  children: React.ReactNode
  scrollToTabQueryParam: boolean
  tabQueryParam: ProductPageTabName | null
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()

  useEffect(() => {
    if (!scrollToTabQueryParam) {
      return
    }

    requestAnimationFrame(() => {
      document.getElementById('product-page-tabordion')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      const href = pathname + '?' + (searchParams ? removeSearchParam(searchParams, 'scrollToTab') : '')
      router.replace(href, { scroll: false })
    })
  }, [tabQueryParam, scrollToTabQueryParam])

  return <React.Fragment>{children}</React.Fragment>
}

export default ProductPageTabordionClient
